/**
 * The procurement confirm cards (create_product and the hardware order card) rendered through the kit's ConfirmCard.
 * Every write is a frozen card: the person confirms exactly what the API froze (scope §6.2); nothing here re-derives
 * the payload. Owner: web-procurement.
 */
import { type CardStatus, ConfirmCard, toast } from '@patchbay/ui'
import * as React from 'react'
import { describeError } from '../../lib/api/client'
import { type Card, procurementApi } from './api'

function statusOf(card: Card, running: boolean): CardStatus {
  if (running) return 'running'
  if (card.state === 'proposed') return 'pending'
  if (card.state === 'confirmed' || card.state === 'edited_confirmed') return 'confirmed'
  if (card.state === 'failed') return 'failed'
  return 'dismissed'
}

function outcomeOf(card: Card): string | undefined {
  const r = card.result
  if (!r) return undefined
  if (r.po_source === 'manual')
    return typeof r.detail === 'string' && r.detail
      ? r.detail
      : 'Recorded. The PO is raised in Books by hand; enter its number on the order.'
  if (typeof r.error === 'string' && r.error === 'books_scope_missing')
    return 'Books refused: the token lacks the purchaseorders scope. Nothing was ordered; reconnect Books with that scope.'
  for (const key of ['outcome', 'message', 'detail', 'error']) if (typeof r[key] === 'string') return r[key] as string
  return undefined
}

export function ProcurementConfirmCard({
  card,
  onChange,
  onSettled,
  children,
}: {
  card: Card
  onChange: (card: Card | null) => void
  /** After a successful confirm: refresh whatever the card changed. */
  onSettled?: (card: Card) => unknown
  children?: React.ReactNode
}) {
  const [running, setRunning] = React.useState(false)
  const inFlight = React.useRef(false)
  const audience = card.audience === 'customer' || card.audience === 'contractor' || card.audience === 'vendor' ? card.audience : 'internal'
  return (
    <ConfirmCard
      kind={card.kind}
      title={card.title ?? card.kind.replaceAll('_', ' ')}
      impact={{ audience, text: card.impact_text }}
      executor={card.executor}
      status={statusOf(card, running)}
      outcome={outcomeOf(card)}
      onConfirm={
        card.can_confirm && card.state === 'proposed'
          ? async () => {
              if (inFlight.current) return
              inFlight.current = true
              setRunning(true)
              try {
                const out = await procurementApi.confirmCard(card.id)
                onChange(out)
                if (out.state !== 'failed') await onSettled?.(out)
              } catch (error) {
                toast.error(`Not confirmed: ${describeError(error)}`)
              } finally {
                inFlight.current = false
                setRunning(false)
              }
            }
          : undefined
      }
      onEdit={
        card.can_confirm && card.editable && card.state === 'proposed'
          ? async () => {
              if (inFlight.current) return
              inFlight.current = true
              setRunning(true)
              try {
                // Rebuilding the form creates a different frozen card. Retire this one first so it cannot be
                // confirmed later from Proposals after the person thought they had edited it.
                await procurementApi.dismissCard(card.id)
                onChange(null)
              } catch (error) {
                toast.error(`Could not reopen for editing: ${describeError(error)}`)
              } finally {
                inFlight.current = false
                setRunning(false)
              }
            }
          : undefined
      }
      onDismiss={
        card.state === 'proposed'
          ? async (reason) => {
              try {
                onChange(await procurementApi.dismissCard(card.id, reason ?? null))
              } catch (error) {
                toast.error(`Not dismissed: ${describeError(error)}`)
              }
            }
          : undefined
      }
    >
      {card.audience === 'vendor' ? <p className="text-xs font-medium">Vendor sees: the purchase order, once Books approves it.</p> : null}
      {children}
      {!card.can_confirm && card.state === 'proposed' ? (
        <p className="mt-1 text-xs text-muted-foreground">Your role cannot confirm this card; a manager can.</p>
      ) : null}
    </ConfirmCard>
  )
}
